class MapSection {
    constructor() {
        this.init();
    }

    init() {
        this.container = document.querySelector("#mapSectionContainer");
        if(!this.container) return;
        this.mapZoom = +this.container.getAttribute("data-map-zoom");
        this.apiKey = this.container.getAttribute("data-api-key");
        this.initialAddress = this.container.getAttribute("data-init-address");
        this.clusterBackgroundColor = this.container.getAttribute("data-cluster-background-color");
        this.clusterTextColor = this.container.getAttribute("data-cluster-text-color");
        this.clusterImage = this.container.getAttribute("data-cluster-image");
        this.mapStyle = this.container.getAttribute("data-mapbox-style");
        this.markersObject = JSON.parse(this.container.querySelector("[data-mapbox-markers]").innerHTML.replace(",]", "]"));
        this.map;
        this.markers = [];
        this.markerClusterMarkers = [];

        this.storeContentWrap = this.container.querySelector("[data-store-content-wrap]");
        this.storeContentEl = this.storeContentWrap.querySelector("[data-store-content]");
        this.storeList = this.container.querySelector("[data-map-section-stores]");
        this.errorsWrapper = this.container.querySelector("[data-map-section-errors]");

        this.initMap();
    }

    async initMap() {
        if(!mapboxgl) {
            return;
        }

        mapboxgl.accessToken = this.apiKey;
        const initCoordResponse = await this.getCoordFromGeocoder(this.initialAddress, 1);

        if (!initCoordResponse) return;

        this.map = new mapboxgl.Map({
            container: 'map',
            style: this.mapStyle === "light" ? 'mapbox://styles/mapbox/light-v11' : 'mapbox://styles/mapbox/dark-v11', 
            center: [...initCoordResponse[0]],
            zoom: this.mapZoom,
        });

        for (let i = 0; i < this.markersObject.length; i++) {
            const marker = this.markersObject[i];
            const markerEl = this.returnMarkerHtml(marker);
            const mapMarker = new mapboxgl.Marker(markerEl);

            if (!marker.address) {
                console.log(`Address for store ${marker.title} is invalid please enter correct address`);
                const error = document.createElement("div");
                error.innerHTML = `Address for store ${marker.title} is invalid please enter correct address`;
                this.errorsWrapper.appendChild(error);

                if (!this.errorsWrapper.classList.contains("show")) {
                    this.errorsWrapper.classList.add("show")
                }

                continue;
            }

            const markerCoordinate = await this.getCoordFromGeocoder(marker.address, 1);

            mapMarker.setLngLat([...markerCoordinate[0]]);

            this.markers.push({
                id: marker.id,
                title: marker.title,
                coordinates: [...markerCoordinate[0]],
                html: `${markerEl.outerHTML}`,
                mapMarker: mapMarker
            })
        }

        this.errorsWrapper.innerHTML = "";

        this.initMapNavigation();
        this.initCluster();
    }

    initCluster() {
        const data = {
            "type": "FeatureCollection",
            "features": [...this.markers.map(marker => {
                return {
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [...marker.coordinates]
                    },
                    properties: {
                        id: marker.id,
                        html: `${marker.html}`
                    }
                }
            })]
        }
        
        this.map.addSource('markers', {
            type: 'geojson',
            data: data,
            cluster: true,
            clusterMaxZoom: 16,
            clusterRadius: 30
        });

        if (this.clusterImage) {
            this.map.loadImage(
                `${this.clusterImage}`, 
                (error, image) => {
                    if (error) throw error;
                    this.map.addImage('clusterImage', image);
            
                    this.map.addLayer({
                        id: 'clusters',
                        type: 'symbol',
                        source: 'markers',
                        filter: ['has', 'point_count'],
                        layout: {
                            "icon-image": "clusterImage",
                            'icon-size': 1,
                            'icon-allow-overlap': true
                        },
                    });

                    this.map.addLayer({
                        id: 'cluster-count',
                        type: 'symbol',
                        source: 'markers',
                        filter: ['has', 'point_count'],
                        layout: {
                            'text-field': ['get', 'point_count_abbreviated'],
                            'text-size': 16,
                            'text-allow-overlap': true
                        },
                        paint: {
                            "text-color": this.clusterTextColor
                        }
                    });
                }
            );
        } else {
            this.map.addLayer({
                id: 'clusters',
                type: 'circle',
                source: 'markers',
                filter: ['has', 'point_count'],
                paint: {
                    'circle-color': [ 'step', ['get', 'point_count'], this.clusterBackgroundColor, 100, this.clusterBackgroundColor, 750, this.clusterBackgroundColor],
                    'circle-radius': [ 'step', ['get', 'point_count'], 20, 100, 30, 750, 40],
                },
            });

            this.map.addLayer({
                id: 'cluster-count',
                type: 'symbol',
                source: 'markers',
                filter: ['has', 'point_count'],
                layout: {
                    'text-field': ['get', 'point_count_abbreviated'],
                    'text-size': 16,
                    'text-allow-overlap': true
                },
                paint: {
                    "text-color": this.clusterTextColor
                }
            });
        }

        const markers = {};
        let markersOnScreen = {};
 
        const updateMarkers = () => {
            const newMarkers = {};
            const features = this.map.querySourceFeatures('markers');
            const parser = new DOMParser();

            for (const feature of features) {
                const coords = feature.geometry.coordinates;
                const props = feature.properties;
                if (props.cluster) continue;
                const id = props.id;
                const markerEl = parser.parseFromString(props.html, "text/html").querySelector("[data-title]");
                let marker = markers[id];

                markerEl.addEventListener("click", () => {
                    const markerId = markerEl.dataset.id.toLowerCase();
                    const markerTitle = markerEl.dataset.title.toLowerCase().replaceAll(" ", "-");
                    const button = document.querySelector(`[data-store-button-id="${markerTitle}"]`);
                    const template = document.querySelector(`[data-template-id="${markerEl.dataset.id}"]`);
                    const content = template.content.firstElementChild.cloneNode(true);
        
                    this.container.querySelectorAll("[data-store-button-id]").forEach(button => button.classList.remove("active"));
        
                    if (button) {
                        if (!button.classList.contains("active")) button.classList.add("active");
                    }

                    this.showStoreInfo(true, content);
                    this.setActiveMarker(markerId)
                })

                if (!marker) {
                    marker = markers[id] = new mapboxgl.Marker({
                        element: markerEl
                    }).setLngLat(coords);
                }
                newMarkers[id] = marker;
                if (!markersOnScreen[id]) marker.addTo(this.map);
            }

            for (const id in markersOnScreen) {
                if (!newMarkers[id]) {
                markersOnScreen[id].remove();
                } 
            }

            markersOnScreen = newMarkers;
        }
 
        this.map.on('render', () => {
            if (!this.map.isSourceLoaded('markers')) return;
            updateMarkers();
        });
    }

    setActiveMarker(markerId) {
        this.markers.forEach((marker) => {
            const markerTitle = marker.title.toLowerCase().replaceAll(" ", "-");

            if (markerTitle === markerId) {
                this.map.panTo(marker.mapMarker.getLngLat(), {
                    animate: true, 
                    duration: 1000,
                    curve: 1.42,
                    speed: 1.2,
                    zoom: this.mapZoom + 4,
                    essential: true
                });
            }
        });
    }

    async getCoordFromGeocoder(search_text, limit) {
        const coordinatesArr = [];
        const responseGeocode = await fetch(`https://api.mapbox.com/search/geocode/v6/forward?access_token=${this.apiKey}&q=${search_text}&limit=${limit}`)
        .then((response) => {
            if (response.ok) {
                return response.json()
            }
        }).catch((e) => {
            console.error(e);
        });

        if (!responseGeocode && responseGeocode.features.length) return;
        
        responseGeocode.features.forEach(feature => {
            coordinatesArr.push(feature.geometry.coordinates);
        })
        
        return coordinatesArr
    }

    returnMarkerHtml(marker) {
        const el = document.createElement("div");
        const pictureEl = document.createElement("picture");
        const imgEl = document.createElement("img");
        imgEl.src = marker.icon;
        pictureEl.appendChild(imgEl);
        el.appendChild(pictureEl);
        el.style.width = "max-content";
        el.style.height = "max-content";
        el.dataset.title = marker.title;
        el.dataset.id = marker.id;

        return el
    }

    initMapNavigation() {
        this.container.addEventListener("click", (e) => {
            if (e.target.closest("[data-store-button-id]")) {
                const target = e.target.closest("[data-store-button-id]");
                const markerId = target.getAttribute("data-store-button-id");
                const template = this.container.querySelector(`[data-store-title-template=${markerId}]`);
                const content = template.content.firstElementChild.cloneNode(true);

                this.container.querySelectorAll("[data-store-button-id]").forEach(button => button.classList.remove("active"));

                if (!target.classList.contains("active")) {
                    target.classList.add("active")
                }

                this.setActiveMarker(markerId);
                this.showStoreInfo(true, content);
            }

            if (e.target.closest("[data-close-store-info]")) {
                this.showStoreInfo(false)
            }
        })
    }

    showStoreInfo(show, content) {
        if (show) {
            if (!this.storeList.classList.contains("no-scroll")) {
                this.storeList.classList.add("no-scroll");
            }

            if (this.errorsWrapper.classList.contains("show")) {
                this.errorsWrapper.classList.remove("show")
            }

            if (!this.storeContentWrap.classList.contains("active")) {
                this.storeContentWrap.classList.add("active");
                this.storeContentWrap.classList.add("opacity-on");
            }

            let timeOut = setTimeout(() => {
                clearTimeout(timeOut);
                this.storeContentWrap.classList.remove("opacity-on");
            }, 300);

            this.updateStoreContent(content, false)
        } else {
            if (this.storeList.classList.contains("no-scroll")) {
                this.storeList.classList.remove("no-scroll");
            }

            this.storeContentWrap.classList.add("opacity-off");

            let timeOut = setTimeout(() => {
                clearTimeout(timeOut);

                if (this.storeContentWrap.classList.contains("active")) {
                    this.storeContentWrap.classList.remove("opacity-off");
                    this.storeContentWrap.classList.remove("active");
                }

                this.updateStoreContent("", true)
            }, 300);
        }

        if (window.innerWidth < 767) {
            document.documentElement.style.overflow = show ? "hidden" : "";
		    document.body.style.overflow = show ? "hidden" : "";
        }
    }

    updateStoreContent(content, clear) {
        this.storeContentEl.childNodes.forEach(node => {
            this.storeContentEl.removeChild(node);
        });

        if (!clear) {
            this.storeContentEl.appendChild(content);
        }
    }
}

document.addEventListener("DOMContentLoaded", () => {
    const mapSection = new MapSection();
})

document.addEventListener("shopify:section:load", () => {
    const mapSection = new MapSection();
})